import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { BariPreNurseSpecialistMedScreeningReportReadData } from './bariPreNurseSpecialistMedScreeningReportData';

export type BariPreNurseSpecialistMedScreeningReportBaseReadKey =
    keyof BariPreNurseSpecialistMedScreeningReportReadData;

export type BariPreNurseSpecialistMedScreeningReportBaseReadLabel =
    | 'Aanvullende onderzoeken'
    | 'Abdominaal'
    | 'Allergieën'
    | 'Antibiotica type'
    | 'Behandeld met antibiotica'
    | 'Bij broer/zus'
    | 'Bij moeder'
    | 'Bij vader'
    | 'Bindweefselziekte (o.a. reumatoïde ziekten)'
    | 'Cerebrovasculaire aandoening'
    | 'Chronische longziekte'
    | 'Conclusie'
    | 'Congestief hartfalen'
    | 'Consistentie ontlasting'
    | 'Consult bij MDL-arts gehad'
    | 'Consult bij internist gehad'
    | 'Contra-indicatie voor een maagverkleinende operatie'
    | 'Datum onderzoek'
    | 'Dementie'
    | 'Details diabetes mellitus'
    | 'Diabetes mellitus'
    | 'Diabetes mellitus (diagnose)'
    | 'Duur overgewicht'
    | 'Dyslipidemie (diagnose)'
    | 'Eerdere operaties'
    | 'Extremiteiten'
    | 'GERD (diagnose)'
    | 'Gastro-intestinaal ulcuslijden'
    | 'Geschikt voor dagbehandeling'
    | 'Gewrichtsklachten (diagnose)'
    | 'HDL-cholesterol'
    | 'HIV/AIDS'
    | 'Hart- en vaatziekten'
    | 'Hartfalen'
    | 'HbA1c'
    | 'Helicobacter pylori onderzoek gehad'
    | 'Hypertensie'
    | 'Hypertensie (diagnose)'
    | 'Klachten van reflux'
    | 'LDL-cholesterol'
    | 'Leverziekte'
    | 'Littekens abdomen'
    | 'Maligniteit (excl. PCC of BCC van de huid)'
    | 'Medicatie'
    | 'Medische voorgeschiedenis'
    | 'Motivatie behandeling'
    | 'Myocardinfarct'
    | 'Nierziekte'
    | 'OSAS (diagnose)'
    | 'Ontlasting frequentie'
    | 'Ontlastingspatroon'
    | 'Overgewicht'
    | 'Para-/hemiplegie'
    | 'Patiënt is mobiel'
    | 'Patiënt is verwezen door'
    | 'Perifeer vaatlijden/aneurysma aorta'
    | 'Smetten'
    | 'Sprake van buikklachten'
    | 'Sprake van helicobacter pylori'
    | 'Toelichting MDL-arts'
    | 'Toelichting actuele medicatie'
    | 'Toelichting ander onderzoek'
    | 'Toelichting ander type operatie'
    | 'Toelichting buikklachten'
    | 'Toelichting cardiologisch onderzoek'
    | 'Toelichting contra-indicatie'
    | 'Toelichting dagbehandeling'
    | 'Toelichting eerdere operaties'
    | 'Toelichting internist'
    | 'Toelichting littekens'
    | 'Toelichting longfunctieonderzoek'
    | 'Toelichting onderzoek interne geneeskunde'
    | 'Toelichting overige consistentie'
    | 'Toelichting slaaponderzoek'
    | 'Toelichting speciële anamnese'
    | 'Toelichting vitaminedeficiënties'
    | 'Triglyceriden'
    | 'Trombose'
    | 'Verhoogd risico DVT of PE'
    | 'Viscerale obesitas'
    | 'Vitaminedeficiënties'
    | 'Wens patiënt voor type operatie';

export type BariPreNurseSpecialistMedScreeningReportReadSectionTitle =
    | 'Aanvullende onderzoeken'
    | 'Anamnese'
    | 'Comorbiditeiten'
    | 'Conclusie'
    | 'Familiaire belasting'
    | 'Lichamelijk onderzoek'
    | 'Medicatie'
    | 'Speciële anamnese';

export const anamneseReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'medische_voorgeschiedenis', label: 'Medische voorgeschiedenis' },
        { key: 'eerdere_operaties', label: 'Eerdere operaties', type: 'boolean' },
        { key: 'eerdere_operaties_toelichting', label: 'Toelichting eerdere operaties' },
        { key: 'consult_internist_gehad', label: 'Consult bij internist gehad', type: 'boolean' },
        { key: 'consult_internist_toelichting', label: 'Toelichting internist' },
        { key: 'consult_MDL_arts_gehad', label: 'Consult bij MDL-arts gehad', type: 'boolean' },
        { key: 'consult_MDL_arts_toelichting', label: 'Toelichting MDL-arts' },
        { key: 'vitaminedeficienties', label: 'Vitaminedeficiënties' },
        { key: 'vitaminedeficienties_toelichting', label: 'Toelichting vitaminedeficiënties' },
        {
            key: 'helicobacter_pylori_onderzoek_gehad',
            label: 'Helicobacter pylori onderzoek gehad',
            type: 'boolean',
        },
        { key: 'helicobacter_pylori_onderzoek_datum', label: 'Datum onderzoek' },
        { key: 'helicobacter_pylori_sprake_van', label: 'Sprake van helicobacter pylori', type: 'boolean' },
        {
            key: 'helicobacter_pylori_behandeld_met_antibiotica',
            label: 'Behandeld met antibiotica',
            type: 'boolean',
        },
        { key: 'helicobacter_pylori_behandeld_met_antibiotica_type', label: 'Antibiotica type' },
    ],
    sectionTitle: 'Anamnese',
};

export const specieleAnamneseReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'patient_is_verwezen_door', label: 'Patiënt is verwezen door' },
        { append: 'jaar', key: 'duur_overgewicht_in_jaren', label: 'Duur overgewicht', type: 'number' },
        { key: 'motivatie_behandeling', label: 'Motivatie behandeling' },
        { key: 'buikklachten', label: 'Sprake van buikklachten', type: 'boolean' },
        { key: 'buikklachten_toelichting', label: 'Toelichting buikklachten' },
        { key: 'ontlastingspatroon', label: 'Ontlastingspatroon' },
        {
            append: 'per week',
            key: 'ontlasting_frequentie_per_week',
            label: 'Ontlasting frequentie',
            type: 'number',
        },
        { key: 'consistentie_ontlasting', label: 'Consistentie ontlasting' },
        { key: 'consistentie_ontlasting_overig', label: 'Toelichting overige consistentie' },
        { key: 'toelichting_speciele_anamnese', label: 'Toelichting speciële anamnese' },
    ],
    sectionTitle: 'Speciële anamnese',
};

export const medicatieReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'medicatie', label: 'Medicatie', type: 'boolean' },
        { key: 'medicatie_toelichting', label: 'Toelichting actuele medicatie' },
        { key: 'allergieen', label: 'Allergieën' },
    ],
    sectionTitle: 'Medicatie',
};

export const familiaireBelastingReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'overgewicht', label: 'Overgewicht', type: 'boolean' },
        { key: 'overgewicht_vader', label: 'Bij vader', type: 'boolean' },
        { key: 'overgewicht_moeder', label: 'Bij moeder', type: 'boolean' },
        { key: 'overgewicht_broer_zus', label: 'Bij broer/zus', type: 'boolean' },
        { key: 'hypertensie', label: 'Hypertensie', type: 'boolean' },
        {
            key: 'hart_en_vaatziekten',
            label: 'Hart- en vaatziekten',
            type: 'boolean',
        },
        { key: 'hartfalen', label: 'Hartfalen', type: 'boolean' },
        { key: 'diabetes_mellitus', label: 'Diabetes mellitus', type: 'boolean' },
    ],
    sectionTitle: 'Familiaire belasting',
};

export const lichamelijkOnderzoekReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'abdominaal', label: 'Abdominaal' },
        { key: 'littekens_abdomen', label: 'Littekens abdomen', type: 'boolean' },
        { key: 'littekens_toelichting', label: 'Toelichting littekens' },
        { key: 'viscerale_obesitas', label: 'Viscerale obesitas', type: 'boolean' },
        { key: 'smetten', label: 'Smetten', type: 'boolean' },
        { key: 'patient_is_mobiel', label: 'Patiënt is mobiel' },
        { key: 'extremiteiten', label: 'Extremiteiten' },
    ],
    sectionTitle: 'Lichamelijk onderzoek',
};

export const aanvullendeOnderzoekenReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'aanvullende_onderzoeken', label: 'Aanvullende onderzoeken' },
        { key: 'cardiologisch_onderzoek_toelichting', label: 'Toelichting cardiologisch onderzoek' },
        { key: 'onderzoek_interne_geneeskunde_toelichting', label: 'Toelichting onderzoek interne geneeskunde' },
        { key: 'slaaponderzoek_toelichting', label: 'Toelichting slaaponderzoek' },
        { key: 'longfunctieonderzoek_toelichting', label: 'Toelichting longfunctieonderzoek' },
        { key: 'ander_onderzoek_toelichting', label: 'Toelichting ander onderzoek' },
    ],
    sectionTitle: 'Aanvullende onderzoeken',
};

export const conclusieReadSection: TypedReadSchema<
    BariPreNurseSpecialistMedScreeningReportBaseReadKey,
    BariPreNurseSpecialistMedScreeningReportBaseReadLabel,
    BariPreNurseSpecialistMedScreeningReportReadSectionTitle
> = {
    items: [
        { key: 'wens_voor_type_operatie', label: 'Wens patiënt voor type operatie' },
        { key: 'wens_voor_type_operatie_anders_toelichting', label: 'Toelichting ander type operatie' },
        {
            key: 'contra_indicatie',
            label: 'Contra-indicatie voor een maagverkleinende operatie',
            type: 'boolean',
        },
        {
            key: 'contra_indicatie_toelichting',
            label: 'Toelichting contra-indicatie',
            type: 'boolean',
        },
        { key: 'dagbehandeling', label: 'Geschikt voor dagbehandeling', type: 'boolean' },
        { key: 'dagbehandeling_toelichting', label: 'Toelichting dagbehandeling' },
        { key: 'conclusie', label: 'Conclusie' },
    ],
    sectionTitle: 'Conclusie',
};
